"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ComplexChoiceItem = exports.ComplexChoiceValidator = void 0;
const validator_1 = require("./validator");
const utils_1 = require("./utils");
const ramda_1 = require("ramda");
const defaultProps = {
    presence: "required"
};
class ComplexChoiceValidator extends validator_1.BaseValidator {
    props;
    constructor(props) {
        super();
        this.props = { ...defaultProps, ...props };
    }
    getAllPossibleInterpretations() {
        const { interpretations = [], options } = this.props;
        const out = interpretations.slice();
        for (const option of Object.values(options)) {
            out.push(...option.getAllPossibleInterpretations());
        }
        return out;
    }
    getAllPossibleTags() {
        const { tags = [], options } = this.props;
        const out = tags.slice();
        for (const option of Object.values(options)) {
            out.push(...option.getAllPossibleTags());
        }
        return out;
    }
    merge(props) {
        return new ComplexChoiceValidator({ ...this.props, ...props });
    }
    interpretation(interpretation) {
        return this.merge((0, utils_1.pushInterpretation)(this.props, interpretation));
    }
    tag(tag) {
        return this.merge((0, utils_1.pushTag)(this.props, tag));
    }
    message(details) {
        return this.merge({
            messages: [...(this.props.messages || []), details]
        });
    }
    expiration(expiration) {
        return this.merge({ expiration });
    }
    unit(unit) {
        return this.merge({ unit });
    }
    default(value) {
        return this.merge({ default: value });
    }
    presence(presence) {
        return this.merge({ presence });
    }
    required() {
        return this.presence("required");
    }
    optional() {
        return this.presence("optional");
    }
    forbidden() {
        return this.presence("forbidden");
    }
    async entry({ rootCtx, ctx, options }) {
        await (0, validator_1.runValidators)({
            rootCtx,
            ctx,
            options,
            validators: [
                this.logProps,
                this.logCustom,
                this.applyDefault,
                this.validateRootPresence,
                this.validateRootType,
                this.validateValuePresence,
                this.validateOptions
            ]
        });
    }
    logProps = async ({ ctx, next }) => {
        const { messages = [], ...props } = this.getPropsToLog();
        ctx.addMessageWithPath(props);
        for (const message of messages) {
            ctx.addMessageWithPath(message);
        }
        return next();
    };
    getPropsToLog() {
        const { options: _, ...props } = this.props;
        return { ...props };
    }
    logCustom = async ({ ctx, next }) => {
        if (typeof ctx.value === "object" &&
            ctx.value !== null &&
            !Array.isArray(ctx.value)) {
            const valueCtx = ctx.createChild("value");
            valueCtx.addMessageWithPath({ values: this.getValues() });
            ctx.mergeLogItems(valueCtx);
        }
        return next();
    };
    applyDefault = async ({ rootCtx, ctx, next, options }) => {
        const { default: defaultValue } = this.props;
        if (options.applyDefaults !== false && ctx.value === undefined) {
            const value = await (0, utils_1.evaluateValue)(rootCtx, ctx, defaultValue);
            ctx.setValue({ value });
        }
        return next();
    };
    validateRootPresence = async ({ rootCtx, ctx, next }) => {
        let presence = await (0, utils_1.evaluateValue)(rootCtx, ctx, this.props.presence);
        if (presence === undefined) {
            presence = "required";
        }
        if (presence === "optional") {
            if (!this.isEmptyRoot(ctx.value)) {
                return next();
            }
        }
        else if (presence === "required") {
            if (!this.isEmptyRoot(ctx.value)) {
                return next();
            }
            ctx.addError({ presence: "required" });
        }
        else {
            if (this.isEmptyRoot(ctx.value)) {
                return;
            }
            if (ctx.path.length !== 0 &&
                !(0, ramda_1.hasPath)(ctx.path, ctx.root)) {
                return;
            }
            ctx.addError({ presence: "forbidden" });
        }
    };
    validateRootType = async ({ ctx, next }) => {
        if (typeof ctx.value === "object" &&
            ctx.value !== null &&
            !Array.isArray(ctx.value)) {
            return next();
        }
        ctx.addError({ type: "object" });
    };
    // NOTE: this method should not only be called for forbidden inputs
    validateValuePresence = async ({ rootCtx, ctx, next }) => {
        let presence = await (0, utils_1.evaluateValue)(rootCtx, ctx, this.props.presence);
        if (presence === undefined) {
            presence = "required";
        }
        const valueCtx = ctx.createChild("value");
        if (!this.isEmptyValue(valueCtx.value)) {
            return next();
        }
        if (presence === "required") {
            valueCtx.addError({ presence: "required" });
            ctx.mergeLogItems(valueCtx);
        }
    };
    validateOptions = async ({ rootCtx, ctx, options, next }) => {
        const { options: items } = this.props;
        let value = ctx.value;
        for (const item of items) {
            if (item.getValue() === ctx.value.value) {
                const valueCtx = ctx.createChild("value");
                const tempCtx = valueCtx.merge({ path: [], value: ctx.value });
                const dataCtx = ctx.createChild("data");
                tempCtx.addMessageWithValue(item.getPropsToLog());
                await item.entry({ rootCtx, ctx: dataCtx, options });
                if (dataCtx.value !== value.data) {
                    if (value === ctx.value) {
                        value = { ...value };
                    }
                    value.data = dataCtx.value;
                }
                valueCtx.mergeLogItems(tempCtx);
                ctx.mergeLogItems(valueCtx);
                ctx.mergeLogItems(dataCtx);
                if (value !== ctx.value) {
                    ctx.value = value;
                }
                return next();
            }
        }
        const valueCtx = ctx.createChild("value");
        valueCtx.addError({ values: this.getValues() });
        ctx.mergeLogItems(valueCtx);
    };
    isEmptyRoot(value) {
        return value === undefined || value === null;
    }
    isEmptyValue(value) {
        return value === undefined || value === null || value === "";
    }
    getValues() {
        return this.props.options.map(option => option.getValue());
    }
}
exports.ComplexChoiceValidator = ComplexChoiceValidator;
class ComplexChoiceItem {
    props;
    constructor(props) {
        this.props = props;
    }
    getAllPossibleInterpretations() {
        const { interpretations = [], data } = this.props;
        const out = interpretations.slice();
        if (data !== undefined) {
            out.push(...data.getAllPossibleInterpretations());
        }
        return out;
    }
    getAllPossibleTags() {
        const { tags = [], data } = this.props;
        const out = tags.slice();
        if (data !== undefined) {
            out.push(...data.getAllPossibleTags());
        }
        return out;
    }
    getPropsToLog() {
        const { value: _1, data: _2, ...props } = this.props;
        return { ...props };
    }
    getValue() {
        return this.props.value;
    }
    async entry(args) {
        return this.props.data.entry(args);
    }
    data(validator) {
        return new ComplexChoiceItem({ ...this.props, data: validator });
    }
    interpretation(interpretation) {
        const { interpretations = [], ...props } = this.props;
        return new ComplexChoiceItem({
            ...props,
            interpretations: [...interpretations, interpretation]
        });
    }
    tag(tag) {
        const { tags = [], ...props } = this.props;
        return new ComplexChoiceItem({ ...props, tags: [...tags, tag] });
    }
}
exports.ComplexChoiceItem = ComplexChoiceItem;
